import { addDays, monthBounds } from '../format/date.js';

/**
 * Hvornaar en kunde faar sin faktura: valget paa /kunder/:id.
 *
 *   manual      controlleren opretter fakturaen paa /fakturering/ny. Standarden.
 *   week        a draft every Monday for the week before, Monday to Sunday.
 *   half_month  en kladde den 1. og den 16. - for 16.-31. og 1.-15.
 *   month       en kladde den 1. for maaneden foer.
 *
 * Kladden laves i Business Central af den automatiske fakturering - samme grundlag og samme
 * buffer som /fakturering/ny - og ligger paa /fakturering til controlleren har set den.
 */
export const INVOICE_SCHEDULES = ['manual', 'week', 'half_month', 'month'] as const;
export type InvoiceSchedule = (typeof INVOICE_SCHEDULES)[number];

export const INVOICE_SCHEDULE_LABELS: Record<InvoiceSchedule, string> = {
  manual: 'Manuelt',
  week: 'Hver uge',
  half_month: 'Hver halve måned',
  month: 'Hver måned',
};

/** En faktureringsperiode, begge datoer med. ISO 8601. */
export interface InvoicePeriod {
  start: string;
  end: string;
}

/**
 * The Monday-to-Sunday week the date is in.
 *
 * Sunday ends the week, so the draft is made on the Monday after, with the whole week behind it.
 * The weeks cross month ends: 26 October to 1 November 2026 is one period.
 */
function weekOf(date: string): InvoicePeriod {
  const [year, month, day] = date.split('-').map(Number);
  /* Built from the parts, like `addDays`: local midnight, so the weekday is the calendar's. */
  const weekday = new Date(year ?? Number.NaN, (month ?? Number.NaN) - 1, day).getDay();
  const start = addDays(date, -((weekday + 6) % 7));
  return { start, end: addDays(start, 6) };
}

/** Perioden i planen, som datoen ligger i. */
export function schedulePeriodOf(
  schedule: Exclude<InvoiceSchedule, 'manual'>,
  date: string,
): InvoicePeriod {
  if (schedule === 'week') return weekOf(date);

  const month = monthBounds(date);
  if (schedule === 'month') return month;

  const sixteenth = `${month.start.slice(0, 8)}16`;
  return date < sixteenth
    ? { start: month.start, end: `${month.start.slice(0, 8)}15` }
    : { start: sixteenth, end: month.end };
}

/** Den seneste periode, der er slut foer `today` - den, en kladde oprettet i dag skal daekke. */
export function lastEndedPeriod(
  schedule: Exclude<InvoiceSchedule, 'manual'>,
  today: string,
): InvoicePeriod {
  return schedulePeriodOf(schedule, addDays(schedulePeriodOf(schedule, today).start, -1));
}

/**
 * De perioder, der er slut og ikke faktureret endnu, aeldste foerst.
 *
 * `lastPeriodEnd` er slutdatoen paa den seneste periode, der er behandlet. Kom serveren ikke op
 * paa dagen, indhentes de perioder, der blev sprunget over - én kladde pr. periode, saa kunden
 * faar det, planen lover.
 *
 * Null - en plan, der aldrig har koert - giver ingen perioder. Planen gaelder **fra nu**: at slaa
 * den til den 10. skal ikke oprette en kladde for sidste maaned med det samme. Kaldstedet saetter
 * `lastPeriodEnd` til `lastEndedPeriod`, naar planen gemmes.
 */
export function duePeriods(
  schedule: InvoiceSchedule,
  lastPeriodEnd: string | null,
  today: string,
): InvoicePeriod[] {
  if (schedule === 'manual' || lastPeriodEnd === null) return [];

  const due: InvoicePeriod[] = [];
  const last = lastEndedPeriod(schedule, today);
  let next = schedulePeriodOf(schedule, addDays(lastPeriodEnd, 1));

  /* Hoejst et par aar bagud. En plan, der har staaet stille laenge, maa ikke blive til hundreder. */
  for (let guard = 0; guard < 48 && next.end <= last.end; guard += 1) {
    due.push(next);
    next = schedulePeriodOf(schedule, addDays(next.end, 1));
  }

  return due;
}
